import { useQuery } from "@tanstack/react-query";
import { CreditCard, Wallet, Banknote, Smartphone } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { useSettings } from "@/lib/SettingsContext";
import { IMG } from "@/lib/images";
import { PageHeader, Disclaimer } from "@/components/Editorial";

const PAY = [
  { Icon: Banknote, label: "Espèces" },
  { Icon: Banknote, label: "Chèque" },
  { Icon: Wallet, label: "Virement" },
  { Icon: CreditCard, label: "Carte bancaire" },
  { Icon: Smartphone, label: "PayPal" },
  { Icon: Smartphone, label: "Wero" },
];

export default function Consultations() {
 const settings = useSettings();

const tarifs = settings?.tarifs?.length ? settings.tarifs : [
{ name: "Voyance", duration: "30 minutes", price: "50 €", note: "" },
{ name: "Contact défunt", duration: "1 heure", price: "80 €", note: "Photo demandée si nécessaire" },
{ name: "Onirothérapie", duration: "1 heure", price: "70 €", note: "" },
{ name: "Numérologie", duration: "Format écrit", price: "45 €", note: "" },
{ name: "Lecture d’âme", duration: "Format écrit", price: "35 €", note: "" },
{ name: "Hypnose Ericksonienne", duration: "1 heure", price: "60 €", note: "" }
];
  const offers = settings?.offers || [];

  useSeo({
  title: "Consultations voyance & médiumnité | Dorothée Lancelot",
description: "Prenez rendez-vous avec Dorothée Lancelot pour une consultation de voyance, contact défunt ou onirothérapie, au bureau, par téléphone ou en visioconférence.",
  
    path: "/consultations",
    keywords: ["consultation médium", "voyance", "contact avec un défunt", "communiquer avec un défunt", "onirothérapie", "numérologie", "lecture d'âme", "hypnose ericksonienne", "tarif consultation médium", "prendre rendez-vous médium", "consultation à distance visio téléphone"],
  });

  return (
    <div>
      <PageHeader
        eyebrow="Accompagnements"
        title="Consultations"
        intro="Dorothée Lancelot propose plusieurs types d'accompagnements et de consultations. Les tarifs sont indiqués clairement ci-dessous."
        image={IMG.libraryCandle}
      />

     <section id="tarifs-charte" className="dl-container py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tarifs.map((t, i) => (
            <div key={i} className="dl-card p-8" data-testid={`tarif-${i}`}>
              <h3 className="serif text-2xl text-[#F3E9D2]">{t.name}</h3>
              {t.duration && <p className="text-sm text-[#F3E9D2]/55 mt-1">{t.duration}</p>}
              <div className="serif text-4xl text-[#D4B978] mt-5">{t.price || <span className="text-xl italic text-[#F3E9D2]/45">Sur demande</span>}</div>
              {t.note && <p className="text-sm text-[#F3E9D2]/60 mt-4 italic">{t.note}</p>}
            </div>
          ))}
        </div>
<p className="mt-6 text-center text-sm text-[#F3E9D2]/75">

Supplément de 5 € le samedi et les jours fériés.
</p>

<div className="mt-12 border border-[rgba(198,167,106,0.30)] p-8">
  <div className="dl-eyebrow mb-4">Charte de valeurs & cadre de consultation</div>

<p className="text-[#F3E9D2]/80 leading-relaxed">
Chaque consultation est menée avec respect, bienveillance, confidentialité
et sans jugement. Je transmets mes ressentis avec sincérité, sans promesse
de résultat et sans chercher à influencer vos décisions.
</p>

<p className="text-[#F3E9D2]/80 leading-relaxed mt-4">
La voyance et la médiumnité reposent sur des perceptions qui ne peuvent
être garanties. En contact défunt, je ne peux promettre la présence d’une
personne précise ni la réception d’un message particulier.
</p>

<p className="text-[#F3E9D2]/80 leading-relaxed mt-4">
Vous restez libre de vos choix. Une consultation ne remplace jamais un avis
médical, psychologique, juridique ou financier.
</p>

<p className="text-[#F3E9D2]/80 leading-relaxed mt-4">
Une consultation réalisée reste due, même si son contenu ne correspond pas
aux attentes ou si le résultat espéré n’est pas obtenu. L’absence de résultat
attendu ne constitue pas, à elle seule, un motif de remboursement, sous réserve
des droits légaux applicables.
</p>
</div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="dl-eyebrow mb-4">Acompte</div>
            <p className="text-[#F3E9D2]/75">Un acompte de <span className="text-[#D4B978] font-medium">{settings?.acompte || "15 %"}</span> est demandé à la réservation.</p>
          </div>
          <div>
            <div className="dl-eyebrow mb-4">Modes de paiement acceptés</div>
            <div className="flex flex-wrap gap-3">
              {PAY.map(({ Icon, label }, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-4 py-2 border border-[rgba(198,167,106,0.3)] text-sm text-[#F3E9D2]/80">
                  <Icon size={14} className="text-[#C6A76A]" /> {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <p className="text-sm text-[#F3E9D2]/55 italic mt-12">
          Les offres spéciales et tarifs ponctuels peuvent varier selon les événements et les périodes.
        </p>

        {offers.length > 0 && (
          <div className="mt-14">
            <div className="dl-eyebrow mb-6">Offres du moment</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {offers.map((o, i) => (
                <div key={i} className="dl-card p-8 border-[rgba(198,167,106,0.4)]" data-testid={`offer-${i}`}>
                  <h4 className="serif text-2xl text-[#D4B978]">{o.title}</h4>
                  {o.description && <p className="text-sm text-[#F3E9D2]/70 mt-3">{o.description}</p>}
                  {o.price && <p className="text-[#D4B978] mt-3">{o.price}</p>}
                  {o.date && <p className="text-xs text-[#F3E9D2]/50 mt-2">{o.date}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <Disclaimer>
          Une consultation ne remplace jamais un avis médical, psychologique, juridique ou financier.
        </Disclaimer>
      </section>
    </div>
  );
}
